import React, { useState, useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import axios from 'axios';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { FaPlus, FaEdit, FaTrash, FaEye, FaSave, FaTimes, FaLock, FaTerminal, FaBold, FaItalic, FaLink, FaImage, FaCode, FaHeading, FaListUl, FaQuoteLeft } from 'react-icons/fa';

const API_URL = 'https://arap-backend.onrender.com/api/blog';

const BlogAdmin = () => {
  const [adminKey, setAdminKey] = useState(localStorage.getItem('blog_admin_key') || '');
  const [authenticated, setAuthenticated] = useState(false);
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editing, setEditing] = useState(null);
  const [showPreview, setShowPreview] = useState(false);
  const [message, setMessage] = useState({ text: '', type: '' });
  const contentRef = useRef(null);

  const emptyPost = {
    title: '',
    content: '',
    excerpt: '',
    coverImage: '',
    tags: '',
    published: false,
  };
  const [form, setForm] = useState(emptyPost);

  const headers = { 'x-admin-key': adminKey };

  const authenticate = () => {
    localStorage.setItem('blog_admin_key', adminKey);
    setAuthenticated(true);
    fetchPosts();
  };

  const fetchPosts = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API_URL}?all=true`, { headers });
      setPosts(res.data);
    } catch (err) {
      if (err.response?.status === 401) {
        setMessage({ text: 'Invalid admin key.', type: 'error' });
        setAuthenticated(false);
        localStorage.removeItem('blog_admin_key');
      } else {
        setMessage({ text: 'Failed to fetch posts.', type: 'error' });
      }
    }
    setLoading(false);
  };

  useEffect(() => {
    if (adminKey && !authenticated) {
      setAuthenticated(true);
      fetchPosts();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const showMsg = (text, type = 'success') => {
    setMessage({ text, type });
    setTimeout(() => setMessage({ text: '', type: '' }), 4000);
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const insertAtCursor = (before, after = '') => {
    const textarea = contentRef.current;
    if (!textarea) return;
    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const selected = form.content.substring(start, end);
    const replacement = before + (selected || 'text') + after;
    const newContent = form.content.substring(0, start) + replacement + form.content.substring(end);
    setForm((prev) => ({ ...prev, content: newContent }));
    setTimeout(() => {
      textarea.focus();
      textarea.selectionStart = start + before.length;
      textarea.selectionEnd = start + before.length + (selected || 'text').length;
    }, 0);
  };

  const toolbarActions = [
    { icon: FaBold, label: 'Bold', action: () => insertAtCursor('**', '**') },
    { icon: FaItalic, label: 'Italic', action: () => insertAtCursor('*', '*') },
    { icon: FaHeading, label: 'Heading', action: () => insertAtCursor('## ') },
    { icon: FaLink, label: 'Link', action: () => insertAtCursor('[', '](https://url)') },
    { icon: FaImage, label: 'Image', action: () => insertAtCursor('![alt](', ')') },
    { icon: FaCode, label: 'Code', action: () => insertAtCursor('```\n', '\n```') },
    { icon: FaListUl, label: 'List', action: () => insertAtCursor('- ') },
    { icon: FaQuoteLeft, label: 'Quote', action: () => insertAtCursor('> ') },
  ];

  const startNewPost = () => {
    setForm(emptyPost);
    setEditing('new');
    setShowPreview(false);
  };

  const startEdit = (post) => {
    setForm({
      title: post.title,
      content: post.content,
      excerpt: post.excerpt,
      coverImage: post.coverImage || '',
      tags: post.tags.join(', '),
      published: post.published,
    });
    setEditing(post.slug);
    setShowPreview(false);
  };

  const cancelEdit = () => {
    setForm(emptyPost);
    setEditing(null);
    setShowPreview(false);
  };

  const savePost = async () => {
    if (!form.title.trim() || !form.content.trim() || !form.excerpt.trim()) {
      showMsg('Title, excerpt, and content are required.', 'error');
      return;
    }

    const payload = {
      ...form,
      tags: form.tags
        .split(',')
        .map((t) => t.trim())
        .filter(Boolean),
    };

    try {
      if (editing === 'new') {
        await axios.post(API_URL, payload, { headers });
        showMsg('Post created successfully!');
      } else {
        await axios.put(`${API_URL}/${editing}`, payload, { headers });
        showMsg('Post updated successfully!');
      }
      cancelEdit();
      fetchPosts();
    } catch (err) {
      if (err.response?.status === 401) {
        showMsg('Unauthorized. Check your admin key.', 'error');
      } else {
        showMsg(`Failed to save: ${err.response?.data?.message || err.message}`, 'error');
      }
    }
  };

  const deletePost = async (slug) => {
    if (!window.confirm('Are you sure you want to delete this post?')) return;
    try {
      await axios.delete(`${API_URL}/${slug}`, { headers });
      showMsg('Post deleted.');
      fetchPosts();
    } catch (err) {
      showMsg('Failed to delete post.', 'error');
    }
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  // Authentication Screen
  if (!authenticated) {
    return (
      <div className="min-h-screen flex items-center justify-center pt-20 px-4">
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          className="terminal-window max-w-md w-full glow-green"
        >
          <div className="terminal-header">
            <div className="terminal-dot bg-cyber-red"></div>
            <div className="terminal-dot bg-cyber-amber"></div>
            <div className="terminal-dot bg-cyber-green"></div>
            <span className="ml-3 text-xs text-cyber-muted">auth_required</span>
          </div>
          <div className="terminal-body py-8">
            <div className="text-center mb-6">
              <FaLock className="text-4xl text-cyber-green mx-auto mb-3" />
              <p className="text-cyber-green font-mono text-sm">AUTHENTICATION REQUIRED</p>
              <p className="text-cyber-muted text-xs mt-1">Enter admin key to access blog management</p>
            </div>
            <div className="space-y-4">
              <input
                type="password"
                value={adminKey}
                onChange={(e) => setAdminKey(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && authenticate()}
                placeholder="Enter admin key..."
                className="w-full px-4 py-3 bg-cyber-black border border-cyber-green/30 rounded text-cyber-green font-mono text-sm placeholder-cyber-muted"
              />
              <button
                onClick={authenticate}
                className="w-full py-3 bg-cyber-green text-cyber-black font-bold rounded hover:bg-cyber-cyan transition-colors"
              >
                {'>'} Authenticate
              </button>
            </div>
            {message.text && (
              <p className={`mt-4 text-sm text-center ${message.type === 'error' ? 'text-cyber-red' : 'text-cyber-green'}`}>
                {message.text}
              </p>
            )}
          </div>
        </motion.div>
      </div>
    );
  }

  // Editor Screen
  if (editing) {
    return (
      <div className="relative pt-20 pb-20 min-h-screen">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            {/* Header */}
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-bold text-white">
                <FaTerminal className="inline mr-2 text-cyber-green" />
                {editing === 'new' ? 'New Dispatch' : 'Edit Dispatch'}
              </h2>
              <div className="flex gap-3">
                <button
                  onClick={() => setShowPreview(!showPreview)}
                  className="px-4 py-2 text-sm border border-cyber-cyan/30 text-cyber-cyan rounded hover:bg-cyber-cyan/10 transition-colors flex items-center gap-2"
                >
                  <FaEye /> {showPreview ? 'Editor' : 'Preview'}
                </button>
                <button
                  onClick={cancelEdit}
                  className="px-4 py-2 text-sm border border-cyber-red/30 text-cyber-red rounded hover:bg-cyber-red/10 transition-colors flex items-center gap-2"
                >
                  <FaTimes /> Cancel
                </button>
              </div>
            </div>

            {message.text && (
              <div className={`mb-4 p-3 rounded text-sm font-mono ${
                message.type === 'error' ? 'bg-cyber-red/10 border border-cyber-red/30 text-cyber-red' : 'bg-cyber-green/10 border border-cyber-green/30 text-cyber-green'
              }`}>
                {message.text}
              </div>
            )}

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Form */}
              <div className="space-y-4">
                <div>
                  <label className="block text-xs text-cyber-green mb-1 font-mono">$ title</label>
                  <input
                    type="text"
                    name="title"
                    value={form.title}
                    onChange={handleChange}
                    placeholder="Post title..."
                    className="w-full px-4 py-3 bg-cyber-dark border border-cyber-green/20 rounded text-white font-mono text-sm placeholder-cyber-muted"
                  />
                </div>

                <div>
                  <label className="block text-xs text-cyber-green mb-1 font-mono">$ excerpt</label>
                  <textarea
                    name="excerpt"
                    value={form.excerpt}
                    onChange={handleChange}
                    placeholder="Short description..."
                    rows={2}
                    className="w-full px-4 py-3 bg-cyber-dark border border-cyber-green/20 rounded text-white font-mono text-sm placeholder-cyber-muted resize-none"
                  />
                </div>

                <div>
                  <label className="block text-xs text-cyber-green mb-1 font-mono">$ cover_image (URL)</label>
                  <input
                    type="text"
                    name="coverImage"
                    value={form.coverImage}
                    onChange={handleChange}
                    placeholder="https://example.com/image.jpg"
                    className="w-full px-4 py-3 bg-cyber-dark border border-cyber-green/20 rounded text-white font-mono text-sm placeholder-cyber-muted"
                  />
                </div>

                <div>
                  <label className="block text-xs text-cyber-green mb-1 font-mono">$ tags (comma separated)</label>
                  <input
                    type="text"
                    name="tags"
                    value={form.tags}
                    onChange={handleChange}
                    placeholder="cybersecurity, pentesting, tools"
                    className="w-full px-4 py-3 bg-cyber-dark border border-cyber-green/20 rounded text-white font-mono text-sm placeholder-cyber-muted"
                  />
                </div>

                <div className="flex items-center gap-3">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      name="published"
                      checked={form.published}
                      onChange={handleChange}
                      className="w-4 h-4 accent-green-500"
                    />
                    <span className="text-sm text-cyber-text">Publish immediately</span>
                  </label>
                </div>

                {/* Toolbar */}
                <div>
                  <label className="block text-xs text-cyber-green mb-1 font-mono">$ content (markdown)</label>
                  <div className="flex flex-wrap gap-1 mb-2 p-2 bg-cyber-panel rounded-t border border-cyber-green/20 border-b-0">
                    {toolbarActions.map((tool) => (
                      <button
                        key={tool.label}
                        onClick={tool.action}
                        title={tool.label}
                        className="p-2 text-cyber-muted hover:text-cyber-green hover:bg-cyber-green/10 rounded transition-colors"
                      >
                        <tool.icon className="text-sm" />
                      </button>
                    ))}
                  </div>
                  <textarea
                    ref={contentRef}
                    name="content"
                    value={form.content}
                    onChange={handleChange}
                    placeholder="Write your post in markdown...&#10;&#10;# Heading&#10;**Bold text**&#10;[Link text](https://url)&#10;![Image alt](https://image-url)"
                    rows={18}
                    className="w-full px-4 py-3 bg-cyber-dark border border-cyber-green/20 rounded-b text-white font-mono text-sm placeholder-cyber-muted resize-y"
                  />
                </div>

                <button
                  onClick={savePost}
                  className="w-full py-3 bg-cyber-green text-cyber-black font-bold rounded hover:bg-cyber-cyan transition-colors flex items-center justify-center gap-2"
                >
                  <FaSave /> {editing === 'new' ? 'Create Post' : 'Update Post'}
                </button>
              </div>

              {/* Preview */}
              <div className="terminal-window h-fit sticky top-20">
                <div className="terminal-header">
                  <div className="terminal-dot bg-cyber-red"></div>
                  <div className="terminal-dot bg-cyber-amber"></div>
                  <div className="terminal-dot bg-cyber-green"></div>
                  <span className="ml-3 text-xs text-cyber-muted">preview.md</span>
                </div>
                <div className="terminal-body max-h-[70vh] overflow-y-auto">
                  {form.coverImage && (
                    <img src={form.coverImage} alt="Cover" className="w-full rounded mb-4 border border-cyber-green/20" />
                  )}
                  <h1 className="text-2xl font-bold text-white mb-2">{form.title || 'Untitled'}</h1>
                  <p className="text-sm text-cyber-muted mb-4 italic">{form.excerpt || 'No excerpt'}</p>
                  {form.tags && (
                    <div className="flex flex-wrap gap-2 mb-4">
                      {form.tags.split(',').map((t) => t.trim()).filter(Boolean).map((tag) => (
                        <span key={tag} className="text-xs px-2 py-1 rounded bg-cyber-green/10 text-cyber-green border border-cyber-green/20">
                          {tag}
                        </span>
                      ))}
                    </div>
                  )}
                  <div className="h-px bg-cyber-green/20 mb-4"></div>
                  <div className="blog-content">
                    <ReactMarkdown remarkPlugins={[remarkGfm]}>
                      {form.content || '*Start writing to see preview...*'}
                    </ReactMarkdown>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    );
  }

  // Dashboard
  return (
    <div className="relative pt-20 pb-20 min-h-screen">
      <div className="max-w-6xl mx-auto px-4 sm:px-6">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
        >
          {/* Header */}
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-8">
            <div>
              <h1 className="text-2xl font-bold text-white flex items-center gap-2">
                <FaTerminal className="text-cyber-green" /> Blog Admin
              </h1>
              <p className="text-sm text-cyber-muted font-mono mt-1">Manage your security dispatches</p>
            </div>
            <div className="flex gap-3">
              <button
                onClick={startNewPost}
                className="px-4 py-2 bg-cyber-green text-cyber-black font-bold rounded hover:bg-cyber-cyan transition-colors flex items-center gap-2 text-sm"
              >
                <FaPlus /> New Post
              </button>
              <button
                onClick={() => { setAuthenticated(false); localStorage.removeItem('blog_admin_key'); }}
                className="px-4 py-2 border border-cyber-red/30 text-cyber-red rounded hover:bg-cyber-red/10 transition-colors text-sm"
              >
                Logout
              </button>
            </div>
          </div>

          {message.text && (
            <div className={`mb-4 p-3 rounded text-sm font-mono ${
              message.type === 'error' ? 'bg-cyber-red/10 border border-cyber-red/30 text-cyber-red' : 'bg-cyber-green/10 border border-cyber-green/30 text-cyber-green'
            }`}>
              {message.text}
            </div>
          )}

          {/* Posts List */}
          {loading ? (
            <div className="text-center py-10">
              <p className="text-cyber-green font-mono animate-pulse">{'>'} Loading posts...</p>
            </div>
          ) : posts.length === 0 ? (
            <div className="terminal-window max-w-md mx-auto">
              <div className="terminal-header">
                <div className="terminal-dot bg-cyber-red"></div>
                <div className="terminal-dot bg-cyber-amber"></div>
                <div className="terminal-dot bg-cyber-green"></div>
                <span className="ml-3 text-xs text-cyber-muted">posts</span>
              </div>
              <div className="terminal-body text-center py-8">
                <p className="text-cyber-muted mb-3">No posts yet.</p>
                <button
                  onClick={startNewPost}
                  className="text-cyber-green hover:text-cyber-cyan transition-colors text-sm"
                >
                  {'>'} Create your first dispatch
                </button>
              </div>
            </div>
          ) : (
            <div className="space-y-3">
              {posts.map((post) => (
                <div
                  key={post._id}
                  className="cyber-card p-4 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3"
                >
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <span className={`inline-block w-2 h-2 rounded-full ${post.published ? 'bg-cyber-green' : 'bg-cyber-amber'}`}></span>
                      <h3 className="text-white font-semibold truncate">{post.title}</h3>
                    </div>
                    <div className="flex flex-wrap items-center gap-3 text-xs text-cyber-muted">
                      <span>{formatDate(post.createdAt)}</span>
                      <span>{post.published ? 'Published' : 'Draft'}</span>
                      {post.tags.length > 0 && (
                        <span className="text-cyber-green">{post.tags.join(', ')}</span>
                      )}
                    </div>
                  </div>
                  <div className="flex gap-2 flex-shrink-0">
                    <a
                      href={`/blog/${post.slug}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="p-2 text-cyber-muted hover:text-cyber-cyan hover:bg-cyber-cyan/10 rounded transition-colors"
                      title="View"
                    >
                      <FaEye />
                    </a>
                    <button
                      onClick={() => startEdit(post)}
                      className="p-2 text-cyber-muted hover:text-cyber-green hover:bg-cyber-green/10 rounded transition-colors"
                      title="Edit"
                    >
                      <FaEdit />
                    </button>
                    <button
                      onClick={() => deletePost(post.slug)}
                      className="p-2 text-cyber-muted hover:text-cyber-red hover:bg-cyber-red/10 rounded transition-colors"
                      title="Delete"
                    >
                      <FaTrash />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </motion.div>
      </div>
    </div>
  );
};

export default BlogAdmin;
